import { shouldPreferPhoneAuth } from '../../../util/googleAuth';
import {
  isInternalWebchatHttpFallbackEligible,
  shouldUseInternalWebchatHttpFallback,
} from '../../../util/internalWebchatHttpFallback';
import { peekPreferredAuthRestartMode } from '../../../util/preferredAuthRestartMode';
import { shouldUsePublicWebchatHttpFallback } from '../../../util/publicWebchatHttpFallback';
import { DEBUG_PHONE_AUTH_MARKER } from '../../../util/debugLoginMarkers';
import { DEBUG } from '../../../config';
import type { ApiInitialArgs } from '../../../api/types';
import type { TransportDebugHookConfig } from '../../../api/types/transportDebugHooks';

type SharedSettingsSnapshot = {
  shouldAllowHttpTransport?: boolean;
  shouldForceHttpTransport?: boolean;
  shouldDebugExportedSenders?: boolean;
  language: string;
};

const PUBLIC_ANDROID_HANDOFF_RETURN_SCHEME = 'runai-public://public-handoff';

function isDebugLoginLane(search = window.location.search): boolean {
  try {
    return new URLSearchParams(search).has('debug_login');
  } catch {
    return false;
  }
}

function hasDebugPhoneAuthMarker(): boolean {
  try {
    return window.sessionStorage.getItem(DEBUG_PHONE_AUTH_MARKER) === '1';
  } catch {
    return false;
  }
}

function isPublicAndroidHandoffLane(search = window.location.search): boolean {
  try {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('run_source') === 'saas'
      && searchParams.get('run_return_to') === PUBLIC_ANDROID_HANDOFF_RETURN_SCHEME;
  } catch {
    return false;
  }
}

function hasExplicitPublicAuthMode(search = window.location.search): boolean {
  try {
    const searchParams = new URLSearchParams(search);
    const auth = searchParams.get('auth');
    return auth === 'phone' || auth === 'qr';
  } catch {
    return false;
  }
}

function isPublicSaasOwnerAccessTokenLane(search = window.location.search): boolean {
  try {
    const searchParams = new URLSearchParams(search);
    return searchParams.get('run_source') === 'saas'
      && Boolean(searchParams.get('ownerAccessToken')?.trim());
  } catch {
    return false;
  }
}

function parseBooleanFlag(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  if (value === '1' || value === 'true') return true;
  if (value === '0' || value === 'false') return false;
  return undefined;
}

function parsePositiveNumber(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }

  return parsed;
}

function buildTransportDebugHook(
  initialLocationHash?: Record<string, string>,
): TransportDebugHookConfig | undefined {
  if (!DEBUG || !initialLocationHash) {
    return undefined;
  }

  const mode = initialLocationHash.transport_hook_mode;
  if (mode !== 'timeout_ping' && mode !== 'close_transport') {
    return undefined;
  }

  return {
    mode,
    triggerOnce: parseBooleanFlag(initialLocationHash.transport_hook_once),
    maxTriggers: parsePositiveNumber(initialLocationHash.transport_hook_max_triggers),
    autoResetMs: parsePositiveNumber(initialLocationHash.transport_hook_auto_reset_ms),
  };
}

export function buildInitApiArgs(params: {
  initialLocationHash?: Record<string, string>;
  pendingWebAuthToken?: string;
  loginToken?: string;
  sharedSettings: SharedSettingsSnapshot;
  userAgent: string;
  platform?: string;
  sessionData?: ApiInitialArgs['sessionData'];
  isWebmSupported?: boolean;
  maxBufferSize?: number;
  isTestServerRequested?: boolean;
  accountIds?: string[];
  hasPasskeySupport?: boolean;
}): ApiInitialArgs {
  const { initialLocationHash, sharedSettings } = params;
  const preferredAuthRestartMode = peekPreferredAuthRestartMode();
  const shouldForcePublicWebchatHttpFallback = shouldUsePublicWebchatHttpFallback()
    && !isPublicSaasOwnerAccessTokenLane()
    && !hasExplicitPublicAuthMode()
    && !isPublicAndroidHandoffLane();
  const shouldForceInternalWebchatHttpFallback = shouldUseInternalWebchatHttpFallback()
    && isInternalWebchatHttpFallbackEligible();
  const shouldDisablePasskeySupport = isDebugLoginLane() || hasDebugPhoneAuthMarker();

  return {
    userAgent: params.userAgent,
    platform: params.platform,
    initialAuthMethod: preferredAuthRestartMode
      || shouldForcePublicWebchatHttpFallback
      || shouldPreferPhoneAuth(window.location.search)
      || hasDebugPhoneAuthMarker()
      ? 'phoneNumber'
      : 'qrCode',
    sessionData: params.sessionData,
    isWebmSupported: params.isWebmSupported,
    maxBufferSize: params.maxBufferSize,
    webAuthToken: initialLocationHash?.tgWebAuthToken || params.pendingWebAuthToken,
    loginToken: params.loginToken,
    transportHookToken: initialLocationHash?.transport_hook_token,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash.tgWebAuthDcId) : undefined,
    mockScenario: initialLocationHash?.mockScenario,
    shouldAllowHttpTransport: shouldForcePublicWebchatHttpFallback
      || shouldForceInternalWebchatHttpFallback
      || sharedSettings.shouldAllowHttpTransport,
    shouldForceHttpTransport: shouldForcePublicWebchatHttpFallback
      || shouldForceInternalWebchatHttpFallback
      || sharedSettings.shouldForceHttpTransport,
    shouldDebugExportedSenders: sharedSettings.shouldDebugExportedSenders,
    transportDebugHook: buildTransportDebugHook(initialLocationHash),
    langCode: sharedSettings.language,
    isTestServerRequested: params.isTestServerRequested,
    accountIds: params.accountIds,
    hasPasskeySupport: shouldDisablePasskeySupport ? false : params.hasPasskeySupport,
  };
}
